import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AuthContext, API } from "@/App";
import axios from "axios";
import {
  ArrowLeft, Users, BookOpen, TrendingUp, Star, 
  Lightbulb, RefreshCw, ChevronDown, ChevronUp,
  GraduationCap, Clock, Award, Brain, LogOut
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { QHubFooter } from "@/components/QHubBranding";
import { MarkdownText } from "@/components/MarkdownText";
import { toast } from "sonner";

const SUBJECT_LABELS = {
  "comunicacion-lenguaje": "Comunicacion y Lenguaje",
  "matematica-datos": "Matematica y Datos",
  "ciencia-tecnologia": "Ciencia y Tecnologia",
  "ciudadania-valores": "Ciudadania y Valores",
};

const BLOOM_COLORS = {
  "Recordar": "bg-gray-100 text-gray-700",
  "Comprender": "bg-blue-100 text-blue-700",
  "Aplicar": "bg-green-100 text-green-700",
  "Analizar": "bg-yellow-100 text-yellow-700",
  "Evaluar": "bg-orange-100 text-orange-700",
  "Crear": "bg-purple-100 text-purple-700",
};

const ChildCard = ({ child, onRequestSuggestions, suggestions, loadingSuggestions }) => {
  const [expanded, setExpanded] = useState(false);
  const productMix = child.product_mix || "qhub_buddy_only";
  const ba = child.buddy_avanza_summary || { has_activity: false };
  const hasBuddyAvanza = ba.has_activity === true;
  const showBuddyCards = productMix === "buddy_avanza_only";
  const showLegacyCards = productMix === "qhub_buddy_only" || productMix === "both" || productMix === "no_activity";
  // Determine which 3-stat header to render:
  // · buddy_avanza_only  → Sesiones · Materias · Última práctica
  // · qhub_buddy_only / both / no_activity → Puntos · Días de Racha · Sesiones (legacy)
  const buddySubjectsWorked = (ba.subjects || []).filter((s) => (s.skills_practiced || 0) > 0).length;
  const lastPracticeAt = ba.last_practice_at || ba.last_training?.ended_at_local;
  const lastPracticeLabel = lastPracticeAt
    ? new Date(lastPracticeAt).toLocaleDateString("es-SV", { day: "numeric", month: "short" })
    : (hasBuddyAvanza ? "—" : "Sin actividad aún");

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
      data-testid={`child-card-${child.student_id}`}
    >
      {/* Header */}
      <div className="p-5 border-b border-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#419cca]/10 to-[#8b5cf6]/10 flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-[#419cca]" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">{child.student_name}</h3>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">{child.grade ? `Grado ${child.grade}` : "Sin grado"}</Badge>
                {hasBuddyAvanza && (
                  <Badge variant="secondary" className="text-xs bg-[#419cca]/10 text-[#419cca]" data-testid={`product-badge-${child.student_id}`}>
                    Buddy Avanza
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-2 rounded-lg hover:bg-gray-50 transition-colors"
            data-testid={`toggle-child-${child.student_id}`}
          >
            {expanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>
        </div>

        {/* Quick Stats — product-aware */}
        {showBuddyCards ? (
          <div className="grid grid-cols-3 gap-3 mt-4">
            <div className="bg-blue-50/60 rounded-xl p-3 text-center">
              <Clock className="w-4 h-4 text-blue-500 mx-auto mb-1" />
              <p className="text-xl font-bold text-gray-900" data-testid={`child-ba-sessions-${child.student_id}`}>{ba.sessions_completed || 0}</p>
              <p className="text-xs text-gray-500">Sesiones</p>
            </div>
            <div className="bg-green-50/60 rounded-xl p-3 text-center">
              <BookOpen className="w-4 h-4 text-green-500 mx-auto mb-1" />
              <p className="text-xl font-bold text-gray-900" data-testid={`child-ba-subjects-${child.student_id}`}>{buddySubjectsWorked}</p>
              <p className="text-xs text-gray-500">Materias trabajadas</p>
            </div>
            <div className="bg-purple-50/60 rounded-xl p-3 text-center">
              <TrendingUp className="w-4 h-4 text-purple-500 mx-auto mb-1" />
              <p className="text-sm font-bold text-gray-900" data-testid={`child-ba-last-${child.student_id}`}>{lastPracticeLabel}</p>
              <p className="text-xs text-gray-500">Última práctica</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3 mt-4">
            <div className="bg-blue-50/60 rounded-xl p-3 text-center">
              <Star className="w-4 h-4 text-blue-500 mx-auto mb-1" />
              <p className="text-xl font-bold text-gray-900">{child.points_total}</p>
              <p className="text-xs text-gray-500">Puntos</p>
            </div>
            <div className="bg-green-50/60 rounded-xl p-3 text-center">
              <TrendingUp className="w-4 h-4 text-green-500 mx-auto mb-1" />
              <p className="text-xl font-bold text-gray-900">{child.streak || 0}</p>
              <p className="text-xs text-gray-500">Dias de Racha</p>
            </div>
            <div className="bg-purple-50/60 rounded-xl p-3 text-center">
              <Clock className="w-4 h-4 text-purple-500 mx-auto mb-1" />
              <p className="text-xl font-bold text-gray-900">{child.sessions_count}</p>
              <p className="text-xs text-gray-500">Sesiones</p>
            </div>
          </div>
        )}
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="p-5 space-y-5">
          {/* Buddy Avanza · Progreso por materia + fortalezas + áreas de refuerzo */}
          {hasBuddyAvanza && (
            <div data-testid={`buddy-avanza-section-${child.student_id}`}>
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Brain className="w-4 h-4" /> Progreso Buddy Avanza
              </h4>
              {typeof ba.accuracy_pct === "number" && (
                <div className="mb-3 bg-blue-50/60 rounded-lg px-3 py-2 inline-block" data-testid={`child-ba-accuracy-${child.student_id}`}>
                  <span className="text-xs text-gray-600">Aciertos en prácticas: </span>
                  <span className="font-bold text-gray-900">{ba.accuracy_pct}%</span>
                </div>
              )}
              <div className="space-y-2">
                {(ba.subjects || []).filter((s) => (s.skills_practiced || 0) > 0).map((s) => (
                  <div key={s.key} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                    <div>
                      <div className="font-medium text-sm text-gray-800">{s.label}</div>
                      <div className="text-xs text-gray-500">{s.skills_practiced} habilidades practicadas</div>
                    </div>
                    <Badge variant="outline" className="text-xs">{s.mastery_label}</Badge>
                  </div>
                ))}
              </div>
              {(() => {
                const allStrong = (ba.subjects || []).flatMap((s) => s.skills_strong || []);
                const allGrowing = (ba.subjects || []).flatMap((s) => s.skills_growing || []);
                return (
                  <>
                    {allStrong.length > 0 && (
                      <div className="mt-3" data-testid={`child-strong-${child.student_id}`}>
                        <h5 className="text-xs font-semibold text-gray-600 mb-1">Fortalezas</h5>
                        <div className="flex flex-wrap gap-1">
                          {allStrong.slice(0, 6).map((sk) => (
                            <Badge key={sk.id} className="bg-green-100 text-green-700 text-xs">{sk.label}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {allGrowing.length > 0 && (
                      <div className="mt-3" data-testid={`child-growing-${child.student_id}`}>
                        <h5 className="text-xs font-semibold text-gray-600 mb-1">Áreas de refuerzo</h5>
                        <div className="flex flex-wrap gap-1">
                          {allGrowing.slice(0, 6).map((sk) => (
                            <Badge key={sk.id} className="bg-amber-100 text-amber-700 text-xs">{sk.label}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
              {(ba.progress_check_last || ba.simulation_1_last || ba.final_simulation_last) && (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {ba.progress_check_last && (
                    <div className="bg-indigo-50 rounded-lg p-2 text-xs">
                      <div className="text-indigo-600 font-semibold">Progress Check</div>
                      <div className="text-gray-700">{ba.progress_check_last.band_label || "Realizado"}</div>
                    </div>
                  )}
                  {ba.simulation_1_last && (
                    <div className="bg-purple-50 rounded-lg p-2 text-xs">
                      <div className="text-purple-600 font-semibold">Simulacro 1</div>
                      <div className="text-gray-700">{ba.simulation_1_last.band_label || ba.simulation_1_last.score_label || "Realizado"}</div>
                    </div>
                  )}
                  {ba.final_simulation_last && (
                    <div className="bg-rose-50 rounded-lg p-2 text-xs">
                      <div className="text-rose-600 font-semibold">Simulacro Final</div>
                      <div className="text-gray-700">{ba.final_simulation_last.band_label || "Realizado"}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Legacy · Niveles Bloom (only for QHUB.BUDDY tradicional) */}
          {!showBuddyCards && Object.keys(child.bloom_summary || {}).length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <Brain className="w-4 h-4" /> Niveles de Bloom Alcanzados
              </h4>
              <div className="flex flex-wrap gap-2">
                {Object.entries(child.bloom_summary).map(([level, count]) => (
                  <Badge key={level} className={BLOOM_COLORS[level] || "bg-gray-100 text-gray-700"}>
                    {level}: {count}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Legacy · Materias activas */}
          {!showBuddyCards && (child.subjects_active || []).length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <BookOpen className="w-4 h-4" /> Materias Activas
              </h4>
              <div className="flex flex-wrap gap-2">
                {child.subjects_active.map((s) => (
                  <Badge key={s} variant="outline" className="text-xs">
                    {SUBJECT_LABELS[s] || s}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Legacy · Actividad reciente */}
          {!showBuddyCards && (child.recent_evidences || []).length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" /> Actividad Reciente
              </h4>
              <div className="space-y-2">
                {child.recent_evidences.slice(0, 3).map((ev, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-3 text-sm">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-gray-800">{ev.topic}</span>
                      <Badge className={BLOOM_COLORS[ev.bloom_level] || "bg-gray-100 text-gray-700"} variant="secondary">
                        {ev.bloom_level}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-500">{SUBJECT_LABELS[ev.subject] || ev.subject}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Parent Support Suggestions · deterministic for Buddy Avanza, AI for legacy */}
      <div className="border-t border-gray-50 p-5">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-amber-500" />
            {hasBuddyAvanza ? "Cómo puedes apoyarle" : "Sugerencias de Apoyo (IA)"}
          </h4>
          {!hasBuddyAvanza && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onRequestSuggestions(child.student_id)}
              disabled={loadingSuggestions}
              className="gap-1.5 text-xs"
              data-testid={`request-suggestions-${child.student_id}`}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loadingSuggestions ? "animate-spin" : ""}`} />
              {suggestions ? "Actualizar" : "Generar"}
            </Button>
          )}
        </div>

        {hasBuddyAvanza && (ba.parent_support_suggestions || []).length > 0 && (
          <div className="space-y-3" data-testid={`parent-support-suggestions-${child.student_id}`}>
            {ba.parent_support_suggestions.map((sug, i) => {
              const hasStructured =
                sug.observacion || sug.como_apoyar || sug.materia || sug.habilidad;
              return (
                <div
                  key={i}
                  className={`rounded-xl p-4 border ${
                    sug.type === "reinforce" ? "bg-amber-50/60 border-amber-100" :
                    sug.type === "maintain" ? "bg-green-50/60 border-green-100" :
                    "bg-gray-50 border-gray-100"
                  }`}
                  data-testid={`support-suggestion-${child.student_id}-${i}`}
                >
                  <div className="font-semibold text-sm text-gray-900 mb-2">{sug.title}</div>
                  {hasStructured ? (
                    <dl className="space-y-1.5 text-sm">
                      {sug.materia && (
                        <div
                          className="flex gap-2"
                          data-testid={`support-suggestion-${child.student_id}-${i}-materia`}
                        >
                          <dt className="font-semibold text-gray-700 min-w-[6rem]">Materia:</dt>
                          <dd className="text-gray-800">{sug.materia}</dd>
                        </div>
                      )}
                      {sug.habilidad && (
                        <div
                          className="flex gap-2"
                          data-testid={`support-suggestion-${child.student_id}-${i}-habilidad`}
                        >
                          <dt className="font-semibold text-gray-700 min-w-[6rem]">Habilidad:</dt>
                          <dd className="text-gray-800">{sug.habilidad}</dd>
                        </div>
                      )}
                      {sug.observacion && (
                        <div
                          className="flex gap-2"
                          data-testid={`support-suggestion-${child.student_id}-${i}-observacion`}
                        >
                          <dt className="font-semibold text-gray-700 min-w-[6rem]">Observación:</dt>
                          <dd className="text-gray-800">{sug.observacion}</dd>
                        </div>
                      )}
                      {sug.como_apoyar && (
                        <div
                          className="flex gap-2"
                          data-testid={`support-suggestion-${child.student_id}-${i}-como-apoyar`}
                        >
                          <dt className="font-semibold text-gray-700 min-w-[6rem]">Cómo apoyar:</dt>
                          <dd className="text-gray-800">{sug.como_apoyar}</dd>
                        </div>
                      )}
                    </dl>
                  ) : (
                    <MarkdownText content={sug.message} />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {loadingSuggestions && (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
            <RefreshCw className="w-4 h-4 animate-spin" />
            Generando sugerencias personalizadas...
          </div>
        )}

        {!hasBuddyAvanza && suggestions && !loadingSuggestions && (
          <div
            className="bg-amber-50/50 rounded-xl p-4"
            data-testid={`suggestions-content-${child.student_id}`}
          >
            <MarkdownText content={suggestions} />
          </div>
        )}

        {!hasBuddyAvanza && !suggestions && !loadingSuggestions && (
          <p className="text-sm text-gray-400 italic">
            Presiona &ldquo;Generar&rdquo; para obtener sugerencias personalizadas de como apoyar a {child.student_name} en casa.
          </p>
        )}
      </div>
    </motion.div>
  );
};

const ParentDashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState({});
  const [loadingSuggestions, setLoadingSuggestions] = useState({});

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        // Padres B2C: su progreso está en Buddy AVANZO (/avanzo/familia).
        const access = await axios.get(`${API}/avanzo-b2c/access`, { withCredentials: true }).catch(() => null);
        if (access?.data?.allowed && access.data.access_type === "b2c") {
          navigate("/avanzo/familia", { replace: true });
          return;
        }
        const res = await axios.get(`${API}/parent/dashboard`, { withCredentials: true });
        setDashboardData(res.data);
      } catch (error) {
        console.error("Error fetching parent dashboard:", error);
        toast.error("Error al cargar el dashboard");
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  const handleRequestSuggestions = async (studentId) => {
    setLoadingSuggestions((prev) => ({ ...prev, [studentId]: true }));
    try {
      const res = await axios.get(`${API}/parent/suggestions/${studentId}`, { withCredentials: true });
      setSuggestions((prev) => ({ ...prev, [studentId]: res.data.suggestions }));
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      toast.error("Error al generar sugerencias");
    } finally {
      setLoadingSuggestions((prev) => ({ ...prev, [studentId]: false }));
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.warn("Logout request failed (continuing anyway):", e);
    }
    window.location.href = "/login";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#419cca] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const children = dashboardData?.children || [];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-blue-50" data-testid="parent-dashboard">


      {/* Header */}
      <header className="bg-gradient-to-r from-[#419cca] to-[#8b5cf6] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <h1 className="text-2xl font-bold font-['Outfit']">Panel de Padres</h1>
              <p className="text-white/70 text-sm">Acompana el aprendizaje de tus hijos</p>
            </div>
            <div className="flex items-center gap-2 bg-white/10 rounded-xl px-3 py-2">
              <Users className="w-4 h-4" />
              <span className="text-sm font-medium">{children.length} {children.length === 1 ? "hijo/a" : "hijos/as"}</span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="text-white/80 hover:text-white hover:bg-white/10"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-8 w-full">
        {children.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Sin perfiles de estudiantes</h3>
              <p className="text-gray-500 mb-4">Aun no hay estudiantes registrados bajo tu cuenta.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Summary Banner — product-aware */}
            {dashboardData?.top_summary_variant === "qhub_buddy_only" && (
              <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="top-summary-qhub">
                <h3 className="text-sm font-semibold text-gray-600 mb-3 flex items-center gap-2">
                  <Award className="w-4 h-4 text-[#419cca]" /> Resumen General
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">
                      {children.reduce((sum, c) => sum + (c.evidence_count || 0), 0)}
                    </p>
                    <p className="text-xs text-gray-500">Total Evidencias</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">
                      {children.reduce((sum, c) => sum + (c.points_total || 0), 0)}
                    </p>
                    <p className="text-xs text-gray-500">Total Puntos</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">
                      {children.reduce((sum, c) => sum + (c.sessions_count || 0), 0)}
                    </p>
                    <p className="text-xs text-gray-500">Total Sesiones</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">
                      {new Set(children.flatMap((c) => c.subjects_active || [])).size}
                    </p>
                    <p className="text-xs text-gray-500">Materias Activas</p>
                  </div>
                </div>
              </div>
            )}
            {dashboardData?.top_summary_variant === "buddy_avanza_only" && (
              <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="top-summary-buddy-avanza">
                <h3 className="text-sm font-semibold text-gray-600 mb-3 flex items-center gap-2">
                  <Award className="w-4 h-4 text-[#419cca]" /> Resumen · Buddy Avanza
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900" data-testid="top-ba-sessions">
                      {children.reduce((sum, c) => sum + ((c.buddy_avanza_summary || {}).sessions_completed || 0), 0)}
                    </p>
                    <p className="text-xs text-gray-500">Sesiones realizadas</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900" data-testid="top-ba-subjects">
                      {new Set(children.flatMap((c) => ((c.buddy_avanza_summary || {}).subjects || []).filter(s => (s.skills_practiced || 0) > 0).map(s => s.key))).size}
                    </p>
                    <p className="text-xs text-gray-500">Materias trabajadas</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900" data-testid="top-ba-simulacros">
                      {children.reduce((sum, c) => sum + ((c.buddy_avanza_summary || {}).simulacros_completed || 0), 0)}
                    </p>
                    <p className="text-xs text-gray-500">Simulacros completados</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-bold text-gray-900" data-testid="top-ba-last">
                      {(() => {
                        const ts = children
                          .map(c => (c.buddy_avanza_summary || {}).last_practice_at
                            || (c.buddy_avanza_summary || {}).last_training?.ended_at_local)
                          .filter(Boolean)
                          .sort().reverse();
                        return ts[0] ? new Date(ts[0]).toLocaleDateString("es-SV", { day: "numeric", month: "short" }) : "Sin actividad aún";
                      })()}
                    </p>
                    <p className="text-xs text-gray-500">Última práctica</p>
                  </div>
                </div>
              </div>
            )}
            {/* mixed / no_activity → no global banner · cada ChildCard muestra lo suyo */}

            {/* Children Cards */}
            {children.map((child) => (
              <ChildCard
                key={child.student_id}
                child={child}
                onRequestSuggestions={handleRequestSuggestions}
                suggestions={suggestions[child.student_id]}
                loadingSuggestions={loadingSuggestions[child.student_id]}
              />
            ))}
          </div>
        )}
      </main>
      <QHubFooter />
    </div>
  );
};

export default ParentDashboard;
